import { motion } from "framer-motion";
import { Blocks, Lock, GitBranch, Fingerprint, Globe2, Scaling } from "lucide-react";

const cards = [
  {
    icon: Blocks,
    title: "Modular architecture",
    text: "Composable identity building blocks — registration, deduplication, credential issuance, authentication — deploy only what your nation needs.",
    span: "md:col-span-2 md:row-span-2",
    big: true,
  },
  { icon: GitBranch, title: "Fully open source", text: "Every component auditable and forkable. Built in the open, governed for the public good." },
  { icon: Lock, title: "Privacy-first", text: "Consent-driven data flows, minimal disclosure, and encryption by default." },
  { icon: Fingerprint, title: "Inclusive biometrics", text: "Designed for real African conditions — offline capture, low bandwidth, all demographics." },
  { icon: Globe2, title: "Interoperable", text: "Open standards and APIs that plug into existing civil registries and DPI stacks." },
  { icon: Scaling, title: "Continental scale", text: "Engineered to enroll and serve populations of hundreds of millions." },
];

const Features = () => (
  <section id="platform" data-testid="features-section" className="relative px-6 sm:px-12 py-32 sm:py-44 border-y border-line">
    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-20">
      <div>
        <div className="flex items-center gap-4 mb-8">
          <span className="h-px w-16 bg-clay" />
          <span className="text-xs uppercase tracking-[0.3em] font-bold text-gold">The Platform</span>
        </div>
        <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight max-w-2xl">
          Everything a nation needs to issue trusted identity.
        </h2>
      </div>
      <p className="max-w-md text-base text-ink-dim leading-relaxed">
        Adulis follows the proven MOSIP model — a neutral, country-owned platform, re-imagined for
        African realities.
      </p>
    </div>

    <div className="grid md:grid-cols-3 md:grid-rows-3 gap-4">
      {cards.map((c, i) => (
        <motion.div
          key={c.title}
          data-testid={`feature-card-${i}`}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, delay: i * 0.08, ease: [0.65, 0, 0.35, 1] }}
          className={`group relative glass-panel p-8 sm:p-10 overflow-hidden hover:bg-raised/80 transition-colors duration-500 ${c.span || ""}`}
        >
          {c.big && (
            <img
              src="https://images.unsplash.com/photo-1526289034009-0240ddb68ce3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-15 group-hover:opacity-25 group-hover:scale-105 transition-[opacity,transform] duration-700"
            />
          )}
          <div className="relative flex flex-col h-full justify-between gap-8">
            <c.icon size={28} className="text-clay group-hover:text-gold transition-colors duration-500" strokeWidth={1.5} />
            <div>
              <h3 className={`font-display font-bold tracking-tight mb-3 ${c.big ? "text-2xl sm:text-3xl" : "text-xl"}`}>{c.title}</h3>
              <p className="text-sm md:text-base text-ink-dim leading-relaxed">{c.text}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  </section>
);

export default Features;
